/* Added the following code for multitenancy_ui.ray using AI Agent. Oct 9, 2026. Code Start */
/**
 * Client helpers for Multitenancy APIs (/api/user/tenant/* and /api/tenant/*).
 */
import { BASE_URL } from "./config";

function authHeaders() {
  const token = localStorage.getItem("token");
  const actAs = localStorage.getItem("actAsTenant");
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
  if (actAs) {
    headers["X-Act-As-Tenant"] = actAs;
  }
  return headers;
}

async function parseJson(response) {
  const status = response.status;
  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  return { status, body };
}

/* Added the following code for deployfix.Ray using AI Agent. 09/10/2026. Code Start */
/**
 * Extract the payload from a tenant API response body.
 * The backend responds `{ success, data }`; `legacyKey` keeps older shapes
 * (e.g. `body.users`, `body.tenant`) working.
 * @param {object|null} body parsed JSON body
 * @param {string} [legacyKey] fallback key used by older responses
 * @returns {*} the payload, or undefined when absent
 */
export function pickData(body, legacyKey) {
  if (!body) return undefined;
  if (body.data !== undefined) return body.data;
  return legacyKey ? body[legacyKey] : undefined;
}
/* Code end. Ray using AI Agent. 09/10/2026 */

/**
 * Fetch current user's tenant profile.
 * Tenant admins and platform admins receive settings, entitlements, and invite code views.
 */
export async function fetchMyTenant() {
  const res = await fetch(`${BASE_URL}/api/user/tenant/me`, {
    headers: authHeaders(),
  });
  return parseJson(res);
}

/**
 * List all invite codes for the active tenant.
 */
export async function fetchMyInviteCodes() {
  const res = await fetch(`${BASE_URL}/api/user/tenant/me/invite-codes`, {
    headers: authHeaders(),
  });
  return parseJson(res);
}

/**
 * Create a new invite code for this tenant.
 * @param {{ role?: string, maxUses?: number, expiresAt?: string }} data
 */
export async function createMyInviteCode(data = {}) {
  const res = await fetch(`${BASE_URL}/api/user/tenant/me/invite-codes`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  return parseJson(res);
}

/**
 * Disable an active invite code immediately.
 * @param {string} code
 */
export async function disableMyInviteCode(code) {
  const res = await fetch(
    `${BASE_URL}/api/user/tenant/me/invite-codes/${encodeURIComponent(code)}/disable`,
    {
      method: "PUT",
      headers: authHeaders(),
    }
  );
  return parseJson(res);
}

/* Commenting the following code for deployfix.Ray using AI Agent. 09/10/2026. Comment Start */
// /**
//  * List all users/learners enrolled in the active tenant.
//  */
// export async function fetchMyTenantUsers() {
//   const res = await fetch(`${BASE_URL}/api/user/tenant/me/users`, {
//     headers: authHeaders(),
//   });
//   return parseJson(res);
// }
/* Commenting end. Ray using AI Agent. 09/10/2026 */

/* Added the following code for deployfix.Ray using AI Agent. 09/10/2026. Code Start */
/**
 * List one page of users/learners enrolled in the active tenant.
 * Response body: `{ success, data: User[], total, page, perPage, totalPages }` (perPage max 200).
 * @param {{ page?: number, perPage?: number }} [options]
 */
export async function fetchMyTenantUsers({ page = 1, perPage = 50 } = {}) {
  const params = new URLSearchParams({ page: String(page), perPage: String(perPage) });
  const res = await fetch(`${BASE_URL}/api/user/tenant/me/users?${params.toString()}`, {
    headers: authHeaders(),
  });
  return parseJson(res);
}
/* Code end. Ray using AI Agent. 09/10/2026 */

/**
 * Fetch monthly AI token usage against allocated budget.
 */
export async function fetchMyTokenUsage() {
  const res = await fetch(`${BASE_URL}/api/user/tenant/me/token-usage`, {
    headers: authHeaders(),
  });
  return parseJson(res);
}

/**
 * Fetch cohort learning progress summary (chapter completion rates).
 */
export async function fetchMyProgressSummary() {
  const res = await fetch(`${BASE_URL}/api/user/tenant/me/progress-summary`, {
    headers: authHeaders(),
  });
  return parseJson(res);
}

/**
 * Public tenant resolution by hostname, slug, or invite code (for custom branding/logos).
 * @param {{ host?: string, slug?: string, code?: string, invite?: string }} query
 */
export async function resolveTenantBranding(query = {}) {
  const params = new URLSearchParams();
  if (query.host) params.set("host", query.host);
  if (query.slug) params.set("slug", query.slug);
  if (query.code) params.set("code", query.code);
  if (query.invite) params.set("invite", query.invite);
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(`${BASE_URL}/api/tenant/resolve${qs}`, {
    headers: { Accept: "application/json" },
  });
  return parseJson(res);
}

/* Added the following code for platform_tenants.ray using AI Agent. 10/10/2026. Code Start */
/**
 * Platform Super Admin: List all tenants across the entire platform with seat & token stats.
 */
export async function fetchPlatformTenants() {
  const res = await fetch(`${BASE_URL}/api/user/platform/tenants`, {
    headers: authHeaders(),
  });
  return parseJson(res);
}

/**
 * Platform Super Admin: Create a new tenant organization.
 * @param {object} data { name, slug, type, domains, branding, entitlements, settings, cloneRoles, inviteCode }
 */
export async function createPlatformTenant(data) {
  const res = await fetch(`${BASE_URL}/api/user/platform/tenants`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  return parseJson(res);
}

/**
 * Platform Super Admin: Fetch a single tenant by id with full details, seats, and AI usage.
 * @param {string} id
 */
export async function fetchPlatformTenant(id) {
  const res = await fetch(`${BASE_URL}/api/user/platform/tenants/${encodeURIComponent(id)}`, {
    headers: authHeaders(),
  });
  return parseJson(res);
}

/**
 * Platform Super Admin: Update tenant settings, branding, domains, or entitlements.
 * @param {string} id
 * @param {object} data
 */
export async function updatePlatformTenant(id, data) {
  const res = await fetch(`${BASE_URL}/api/user/platform/tenants/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  return parseJson(res);
}

/**
 * Platform Super Admin: Suspend or reactivate a tenant organization.
 * @param {string} id
 * @param {"active"|"suspended"} status
 */
export async function setPlatformTenantStatus(id, status) {
  const res = await fetch(`${BASE_URL}/api/user/platform/tenants/${encodeURIComponent(id)}/status`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify({ status }),
  });
  return parseJson(res);
}

/**
 * Platform Super Admin: Create an invite code directly on any tenant.
 * @param {string} id
 * @param {{ role?: string, maxUses?: number, expiresAt?: string, code?: string }} data
 */
export async function createPlatformTenantInviteCode(id, data = {}) {
  const res = await fetch(`${BASE_URL}/api/user/platform/tenants/${encodeURIComponent(id)}/invite-codes`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  return parseJson(res);
}

/**
 * Platform Super Admin: Disable an invite code on any tenant.
 * @param {string} id
 * @param {string} code
 */
export async function disablePlatformTenantInviteCode(id, code) {
  const res = await fetch(
    `${BASE_URL}/api/user/platform/tenants/${encodeURIComponent(id)}/invite-codes/${encodeURIComponent(code)}/disable`,
    {
      method: "PUT",
      headers: authHeaders(),
    }
  );
  return parseJson(res);
}
/* Code end. ray using AI Agent. 10/10/2026 */
/* Code end. ray using AI Agent. Oct 9, 2026 */
